import { Button, Colors, Icon, InputGroup, Popover, Position } from '@blueprintjs/core'
import * as React from 'react'
import {
  ancestorPathsForCategory,
  buildCategoryTree,
  CategoryTreeNode,
  filterCategoriesByQuery,
  FlatCategory,
  indexCategoriesById,
  parentPathDisplay,
  toggleExpandedPaths
} from './categoryTree'

export interface ExpressListCategorySelectProps {
  load: () => Promise<{ data: { categories: FlatCategory[] } }>
  value: number | null
  onChange: (categoryId: number | null) => void
  autoload?: boolean
  disabled?: boolean
}

const RECENT_STORAGE_KEY = 'express-list-recent-category-ids'
const RECENT_LIMIT = 3

function loadRecentCategoryIds(): number[] {
  try {
    const raw = localStorage.getItem(RECENT_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'number') : []
  } catch {
    return []
  }
}

function saveRecentCategoryId(categoryId: number) {
  const recent = loadRecentCategoryIds().filter(id => id !== categoryId)
  localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify([categoryId, ...recent].slice(0, RECENT_LIMIT)))
}

interface CategoryTreeRowsProps {
  categories: CategoryTreeNode[]
  expandedPaths: Set<string>
  selectedId: number | null
  onToggleExpand: (pathCache: string) => void
  onSelect: (categoryId: number) => void
}

const CategoryTreeRows: React.FC<CategoryTreeRowsProps> = ({
  categories,
  expandedPaths,
  selectedId,
  onToggleExpand,
  onSelect
}) => (
  <>
    {categories.map(category => {
      const hasChildren = category.children.length > 0
      const isExpanded = expandedPaths.has(category.pathCache)
      const isSelected = selectedId === category.id
      const isSelectable = category.selectable !== false

      const activateRow = () => {
        if (isSelectable) {
          onSelect(category.id)
          return
        }

        if (hasChildren && !isExpanded) {
          onToggleExpand(category.pathCache)
        }
      }

      return (
        <React.Fragment key={category.pathCache}>
          <div
            role="treeitem"
            aria-selected={isSelectable ? isSelected : undefined}
            aria-expanded={hasChildren ? isExpanded : undefined}
            tabIndex={0}
            className={[
              'express-list-category-select-tree-row',
              isSelected ? 'express-list-category-select-tree-row-selected' : '',
              !isSelectable ? 'express-list-category-select-tree-row-structural' : ''
            ]
              .filter(Boolean)
              .join(' ')}
            data-testid={`express-list-category-tree-row-${category.id}`}
            onClick={activateRow}
            onKeyDown={event => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                activateRow()
              }
            }}
          >
            {hasChildren ? (
              <button
                type="button"
                className="express-list-category-select-tree-toggle"
                aria-label={isExpanded ? `Collapse ${category.name}` : `Expand ${category.name}`}
                data-testid={`express-list-category-tree-toggle-${category.id}`}
                onClick={event => {
                  event.stopPropagation()
                  onToggleExpand(category.pathCache)
                }}
              >
                <Icon icon={isExpanded ? 'chevron-down' : 'chevron-right'} color={Colors.LIGHT_GRAY2} />
              </button>
            ) : (
              <span className="express-list-category-select-tree-toggle-spacer" />
            )}
            <span className="express-list-category-select-tree-label">{category.name}</span>
            {isSelected ? (
              <Icon className="express-list-category-select-tree-check" icon="tick" color={Colors.GREEN3} />
            ) : (
              hasChildren && (
                <span className="express-list-category-select-tree-meta">{category.children.length} subcategories</span>
              )
            )}
          </div>
          {hasChildren && isExpanded && (
            <div className="express-list-category-select-tree-children" role="group">
              <CategoryTreeRows
                categories={category.children}
                expandedPaths={expandedPaths}
                selectedId={selectedId}
                onToggleExpand={onToggleExpand}
                onSelect={onSelect}
              />
            </div>
          )}
        </React.Fragment>
      )
    })}
  </>
)

export const ExpressListCategorySelect: React.FC<ExpressListCategorySelectProps> = ({
  load,
  value,
  onChange,
  autoload = true,
  disabled = false
}) => {
  const [loading, setLoading] = React.useState(false)
  const [tree, setTree] = React.useState<CategoryTreeNode[]>([])
  const [flatCategories, setFlatCategories] = React.useState<FlatCategory[]>([])
  const [categoriesById, setCategoriesById] = React.useState<Map<number, FlatCategory>>(new Map())
  const [isOpen, setIsOpen] = React.useState(false)
  const [expandedPaths, setExpandedPaths] = React.useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = React.useState('')
  const [highlightedSearchIndex, setHighlightedSearchIndex] = React.useState(-1)
  const [recentCategoryIds, setRecentCategoryIds] = React.useState<number[]>([])
  const searchInputRef = React.useRef<HTMLInputElement>(null)
  const menuBodyRef = React.useRef<HTMLDivElement>(null)

  const [loaded, setLoaded] = React.useState(false)

  const fetchCategories = React.useCallback(async () => {
    if (loaded) return

    setLoading(true)
    try {
      const res = await load()
      const flat = res.data.categories
      setTree(buildCategoryTree(flat))
      setFlatCategories(flat)
      setCategoriesById(indexCategoriesById(flat))
      setLoaded(true)
    } finally {
      setLoading(false)
    }
  }, [load, loaded])

  React.useEffect(() => {
    if (autoload) fetchCategories()
  }, [autoload, fetchCategories])

  React.useEffect(() => {
    if (isOpen) {
      searchInputRef.current?.focus()
      setRecentCategoryIds(loadRecentCategoryIds().filter(id => categoriesById.has(id)))
      if (value != null) {
        const selected = categoriesById.get(value)
        if (selected) {
          setExpandedPaths(new Set(ancestorPathsForCategory(selected.pathCache)))
        }
      }
    }
  }, [isOpen, value, flatCategories, categoriesById])

  const selectedCategory = value != null ? categoriesById.get(value) : undefined
  const trimmedSearchQuery = searchQuery.trim()
  const searchResults = React.useMemo(
    () => filterCategoriesByQuery(flatCategories, searchQuery),
    [flatCategories, searchQuery]
  )
  const visibleRecentCategoryIds = recentCategoryIds.filter(id => categoriesById.has(id))

  const resetMenuState = () => {
    setSearchQuery('')
    setHighlightedSearchIndex(-1)
    setExpandedPaths(new Set())
  }

  const closeMenu = () => {
    setIsOpen(false)
    resetMenuState()
  }

  const selectCategory = (categoryId: number) => {
    saveRecentCategoryId(categoryId)
    onChange(categoryId)
    closeMenu()
  }

  const handleToggleExpand = (pathCache: string) => {
    setExpandedPaths(current => toggleExpandedPaths(current, tree, pathCache))
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      if (trimmedSearchQuery) {
        setSearchQuery('')
        setHighlightedSearchIndex(-1)
      } else {
        closeMenu()
      }
      return
    }

    if (!trimmedSearchQuery || searchResults.length === 0) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setHighlightedSearchIndex(current => Math.min(current + 1, searchResults.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlightedSearchIndex(current => Math.max(current - 1, 0))
    } else if (event.key === 'Enter' && highlightedSearchIndex >= 0) {
      event.preventDefault()
      selectCategory(searchResults[highlightedSearchIndex].id)
    }
  }

  const handleMenuKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape' && event.target === searchInputRef.current) {
      return
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      closeMenu()
    }
  }

  const handleOpenChange = (nextOpen: boolean, event?: React.SyntheticEvent<HTMLElement>) => {
    if (nextOpen) {
      if (!isOpen) {
        resetMenuState()
        setIsOpen(true)
        if (tree.length === 0) fetchCategories()
      }
      return
    }

    if (event && 'key' in event && event.key === 'Escape') {
      closeMenu()
      return
    }

    if (
      !event ||
      (event.target instanceof Element &&
        (event.target.closest('.express-list-category-select-menu') ||
          event.target.closest('.express-list-category-select-menu-wrapper') ||
          event.target.closest('.express-list-category-select-search')))
    ) {
      return
    }

    closeMenu()
  }

  const renderSearchResultRow = (category: FlatCategory, index: number) => {
    const parentDisplay = parentPathDisplay(category.pathCache)
    const isHighlighted = index === highlightedSearchIndex

    return (
      <div
        key={category.id}
        role="option"
        aria-selected={isHighlighted}
        tabIndex={0}
        className={[
          'express-list-category-select-search-row',
          isHighlighted ? 'express-list-category-select-search-row-active' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        data-testid={`express-list-category-search-row-${category.id}`}
        onClick={() => selectCategory(category.id)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            selectCategory(category.id)
          }
        }}
      >
        <div className="express-list-category-select-search-row-text">
          <div className="express-list-category-select-search-row-name">{category.name}</div>
          {parentDisplay && <div className="express-list-category-select-search-row-path">{parentDisplay}</div>}
        </div>
        {isHighlighted && <Icon icon="arrow-down" className="express-list-category-select-search-row-enter" />}
      </div>
    )
  }

  const renderRecentRow = (categoryId: number) => {
    const category = categoriesById.get(categoryId)
    if (!category) return null

    const parentDisplay = parentPathDisplay(category.pathCache)

    return (
      <div
        key={category.id}
        role="option"
        aria-selected={false}
        tabIndex={0}
        className="express-list-category-select-recent-row"
        data-testid={`express-list-category-recent-row-${category.id}`}
        onClick={() => selectCategory(category.id)}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            selectCategory(category.id)
          }
        }}
      >
        <Icon icon="time" className="express-list-category-select-recent-icon" color={Colors.GRAY3} />
        <div className="express-list-category-select-recent-text">
          <span className="express-list-category-select-recent-name">{category.name}</span>
          {parentDisplay && <span className="express-list-category-select-recent-path">{parentDisplay}</span>}
        </div>
      </div>
    )
  }

  const renderBrowseBody = () => (
    <>
      {visibleRecentCategoryIds.length > 0 && (
        <>
          <div className="express-list-category-select-section">Recent</div>
          {visibleRecentCategoryIds.map(renderRecentRow)}
        </>
      )}
      <div className="express-list-category-select-section">All categories</div>
      <div role="tree" className="express-list-category-select-tree">
        <CategoryTreeRows
          categories={tree}
          expandedPaths={expandedPaths}
          selectedId={value}
          onToggleExpand={handleToggleExpand}
          onSelect={selectCategory}
        />
      </div>
    </>
  )

  const renderSearchBody = () => {
    if (searchResults.length > 0) {
      return (
        <>
          <div className="express-list-category-select-section">Results</div>
          {searchResults.map((category, index) => renderSearchResultRow(category, index))}
        </>
      )
    }

    return (
      <>
        <div className="express-list-category-select-empty-search">
          <p className="express-list-category-select-empty-search-title">No matching categories</p>
          <p className="express-list-category-select-empty-search-subtitle">
            Try another keyword or browse the list below.
          </p>
        </div>
        <div className="express-list-category-select-section">All categories</div>
        <div role="tree" className="express-list-category-select-tree">
          <CategoryTreeRows
            categories={tree}
            expandedPaths={expandedPaths}
            selectedId={value}
            onToggleExpand={handleToggleExpand}
            onSelect={selectCategory}
          />
        </div>
      </>
    )
  }

  const buttonText = selectedCategory ? (
    <>
      <span className="express-list-category-select-button-name">{selectedCategory.name}</span>
      {parentPathDisplay(selectedCategory.pathCache) && (
        <span className="express-list-category-select-button-path">
          {' · '}
          {parentPathDisplay(selectedCategory.pathCache)}
        </span>
      )}
    </>
  ) : (
    'Select'
  )

  const menu = (
    <div
      className="express-list-category-select-menu-wrapper"
      onClick={event => event.stopPropagation()}
      onKeyDown={handleMenuKeyDown}
    >
      <div className="express-list-category-select-search">
        <InputGroup
          inputRef={searchInputRef}
          leftIcon="search"
          placeholder="Search categories"
          value={searchQuery}
          onChange={event => {
            setSearchQuery(event.target.value)
            setHighlightedSearchIndex(-1)
          }}
          onKeyDown={handleSearchKeyDown}
          data-testid="express-list-category-select-search"
        />
      </div>
      <div ref={menuBodyRef} className="express-list-category-select-menu" role="listbox">
        {trimmedSearchQuery ? renderSearchBody() : renderBrowseBody()}
      </div>
    </div>
  )

  const handleClear = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    closeMenu()
    onChange(null)
  }

  return (
    <div className="express-list-category-select-control">
      <Popover
        isOpen={isOpen}
        onInteraction={handleOpenChange}
        position={Position.BOTTOM_LEFT}
        minimal
        content={menu}
        popoverClassName="express-list-category-select-popover bp3-dark"
      >
        <Button
          data-testid="express-list-category-select-button"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          loading={loading}
          disabled={disabled}
          style={{ width: '100%', justifyContent: 'space-between' }}
          alignText="left"
          rightIcon="caret-down"
        >
          {buttonText}
        </Button>
      </Popover>
      {selectedCategory && (
        <Button
          minimal
          small
          icon="cross"
          aria-label="Clear value"
          className="express-list-category-select-clear"
          disabled={disabled}
          onClick={handleClear}
        />
      )}
    </div>
  )
}
